import type { EventBus, GameEvents } from '@/contracts';

type Handler = (p: never) => void;

/**
 * Typed synchronous event bus.
 *
 * Handlers added during a dispatch are not called for that dispatch, and handlers
 * removed during a dispatch are not called afterwards — we iterate a snapshot and
 * re-check membership, which keeps `off()`-inside-a-handler safe.
 */
export class Bus implements EventBus {
  private readonly map = new Map<string, Set<Handler>>();

  on<K extends keyof GameEvents>(k: K, fn: (p: GameEvents[K]) => void): () => void {
    let s = this.map.get(k as string);
    if (!s) this.map.set(k as string, (s = new Set()));
    s.add(fn as Handler);
    return () => this.off(k, fn);
  }

  once<K extends keyof GameEvents>(k: K, fn: (p: GameEvents[K]) => void): () => void {
    const off = this.on(k, (p) => {
      off();
      fn(p);
    });
    return off;
  }

  off<K extends keyof GameEvents>(k: K, fn: (p: GameEvents[K]) => void): void {
    this.map.get(k as string)?.delete(fn as Handler);
  }

  emit<K extends keyof GameEvents>(k: K, p: GameEvents[K]): void {
    const s = this.map.get(k as string);
    if (!s || s.size === 0) return;
    for (const fn of [...s]) {
      if (!s.has(fn)) continue;
      try {
        (fn as (x: GameEvents[K]) => void)(p);
      } catch (err) {
        // One bad listener must not stall the frame or starve its siblings.
        console.error(`[events] handler for "${String(k)}" threw`, err);
      }
    }
  }

  clear(): void {
    this.map.clear();
  }
}
